import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectAccountChatRoomId } from 'selectors/app/selectAccountPreferences'
import { selectCurrentUserPrefersChatAutoAssignOnNote } from 'ducks/currentUser/selectors/preferences/selectCurrentUserPrefersChatAutoAssignOnNote'
import { selectCurrentUserPrefersChatAutoAssignOnReply } from 'ducks/currentUser/selectors/preferences/selectCurrentUserPrefersChatAutoAssignOnReply'
import { selectIsChatAvailableForCurrentWidget } from 'ducks/widgets/selectors'
import { convertToShadow } from '../utils/events'
import { getMatrixClient, fireEvent } from '../utils/client'
import { AGENT_USER_REGEX } from '../utils/constants'
import { useMatrixRoomById } from './rooms'

const checkIsNote = ({ editorModel }) => {
  const [firstPart] = editorModel.parts
  return firstPart && firstPart.text.startsWith('**')
}

export function useChatComposer(roomId) {
  const room = useMatrixRoomById(roomId)
  const [isParticipant, setIsParticipant] = useState(false)
  const [hasRecentlyAssigned, setHasRecentlyAssigned] = useState(false)
  const accountChatRoomId = useSelector(selectAccountChatRoomId)
  const autoAssignOnReply = useSelector(
    selectCurrentUserPrefersChatAutoAssignOnReply
  )
  const autoAssignOnNote = useSelector(
    selectCurrentUserPrefersChatAutoAssignOnNote
  )
  const enabled = useSelector(selectIsChatAvailableForCurrentWidget)

  const autoAssignUser = useCallback(
    async isNote => {
      if (hasRecentlyAssigned) return
      const { currentState } = room
      const client = getMatrixClient()

      // Ensure that the user who is responding is a participant of the room
      const agentId = client.getUserId().match(AGENT_USER_REGEX).groups.agent_id

      // Ensure that permanent agent id gets set if its not already set
      const {
        event: {
          content: assignContent = {},
          content: {
            permanent_agent_id: permanentAgentId,
            type: assignType,
            agent_id: assignedId,
            team_id: assignedTeamId,
          } = {},
        } = {},
      } =
        currentState.getStateEvents('g.assign', '') || {}

      const isRoomStateAssignable = !assignType || assignType === 'none'
      const isAutoAssignedAllowed =
        (!isNote && autoAssignOnReply) || (isNote && autoAssignOnNote)

      // If the room is already assigned to an agnt or the user doesntn allow
      // auto assignment, then just break out here
      const shouldAssignAgent = isRoomStateAssignable && isAutoAssignedAllowed

      if (!permanentAgentId || shouldAssignAgent) {
        await fireEvent(
          roomId,
          {
            type: 'g.assign',
            content: {
              ...assignContent,
              permanent_agent_id: permanentAgentId || agentId,
              type: shouldAssignAgent ? 'agent' : assignType,
              agent_id: shouldAssignAgent ? agentId : assignedId,
              team_id: shouldAssignAgent ? null : assignedTeamId,
              auto_assign_attempt: 0,
              actor: null,
            },
            stateKey: '',
          },
          0
        )
      }
      setHasRecentlyAssigned(true)
      setTimeout(() => {
        setHasRecentlyAssigned(false)
      }, 1000)
    },
    [autoAssignOnReply, autoAssignOnNote, hasRecentlyAssigned, room, roomId]
  )

  const ensureUserIsParticipant = useCallback(
    async () => {
      if (isParticipant) return
      const { currentState } = room
      const client = getMatrixClient()

      // Ensure that the user who is responding is a participant of the room
      const agentId = client.getUserId().match(AGENT_USER_REGEX).groups.agent_id
      const { event: { content: { agent_ids: agentIds = [] } = {} } = {} } =
        currentState.getStateEvents('g.participants', '') || {}

      if (!agentIds.includes(agentId)) {
        await fireEvent(
          roomId,
          {
            type: 'g.participants',
            content: {
              agent_ids: [...agentIds, agentId],
              lastUpdated: new Date().getTime(),
            },
            stateKey: '',
          },
          0
        )
      }
      setIsParticipant(true)
    },
    [isParticipant, room, roomId]
  )

  const prepareMessage = useCallback(
    async data => {
      // eslint-disable-next-line no-param-reassign
      data.accountChatRoomId = accountChatRoomId
      let { content } = data
      const isNote = checkIsNote(data)
      await autoAssignUser(isNote)

      if (isNote) {
        content = await convertToShadow(data)
      } else {
        await ensureUserIsParticipant(data)
      }

      return content
    },
    [accountChatRoomId, ensureUserIsParticipant, autoAssignUser]
  )

  return {
    room,
    enabled,
    accountChatRoomId,
    prepareMessage,
  }
}
